import React, { useContext, useEffect } from 'react';
import { ToastContext } from '../../contexts/toast-context-with-provider';
import PropTypes from 'prop-types';

export default function GeneralWebPage ({ htmlContent }) {
  const { showToast } = useContext(ToastContext);
  const apiEndpoint = `${process.env.NEXT_PUBLIC_API_PATH}/course-details-enquiry`;
  
  const showFieldError = (fieldElement, message, colorClass = 'text-red-500') => {
    if (!fieldElement) return;
    console.log("showFieldError called with message:", message);
    const existingError = fieldElement.parentElement?.querySelector('.field-error');
    if (existingError) {
      existingError.remove();
    }

    if (message) {
      const errorElement = document.createElement('div');
      errorElement.className = `field-error ${colorClass} text-sm mt-1`;
      errorElement.textContent = message;
      fieldElement.parentElement?.appendChild(errorElement);
    }
  };

  const clearFieldError = (fieldElement) => {
    if (!fieldElement) return;
    
    const existingError = fieldElement.parentElement?.querySelector('.field-error');
    if (existingError) {
      existingError.remove();
    }
  };

  const onlyLettersAndSpace = (str) => {
    return /^[A-Za-z\s]+$/.test(str);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validation functions
  const validateName = (name) => {
    if (!name || name.trim() === '') {
      return 'Name is required!';
    }
    if (!onlyLettersAndSpace(name)) {
      return 'Number or special characters are not allowed!';
    }
    return '';
  };

  const validateEmailField = (email) => {
    if (!email || email.trim() === '') {
      return 'Email is required!';
    }
    if (!validateEmail(email)) {
      return 'Invalid email address!';
    }
    return '';
  };

  // eslint-disable-next-line radix
  const validateContact = (contact) => {
    if (!contact || contact.trim() === '') {
      return 'Contact number is required!';
    }
    if (contact.length !== 10) {
      return 'Invalid digits of mobile!';
    }
    if (!/^\d+$/.test(contact)) {
      return 'Contact number must contain only digits';
    }
    if (parseInt(contact[0]) < 6) {
      return 'Invalid mobile number!';
    }
    return '';
  };

  const validateEducation = (education) => {
    if (!education || education === '') {
      return 'Education is required!';
    }
    return '';
  };

  const validateState = (state) => {
    if (!state || state === '') {
      return 'State is required!';
    }
    return '';
  };

 useEffect(() => {
  let cleanupFn;
  const attachHandler = () => {
    const form = document.getElementById("enquiryForm");
    if (!form || form.dataset.listenerAttached === "true") return;
    const nameInput = document.querySelector('input[placeholder="Name"]') || document.getElementById('name');
    const emailInput = document.querySelector('input[placeholder="Email"]') || document.getElementById('email');
    const contactInput = document.querySelector('input[placeholder="Contact Number"]') || document.getElementById('contactNumber');
    const educationSelect = document.querySelectorAll('select')[0] || document.getElementById('education');
    const stateSelect = document.querySelectorAll('select')[1] || document.getElementById('state');

    const clearNameError = () => clearFieldError(nameInput);
    const clearEmailError = () => clearFieldError(emailInput);
    const clearContactError = () => clearFieldError(contactInput);
    const clearEducationError = () => clearFieldError(educationSelect);
    const clearStateError = () => clearFieldError(stateSelect);

    nameInput?.addEventListener('input', clearNameError);
    nameInput?.addEventListener('change', clearNameError);
    emailInput?.addEventListener('input', clearEmailError);
    emailInput?.addEventListener('change', clearEmailError);
    contactInput?.addEventListener('input', clearContactError);
    contactInput?.addEventListener('change', clearContactError);
    educationSelect?.addEventListener('change', clearEducationError);
    stateSelect?.addEventListener('change', clearStateError);

    const handleSubmit = (event) => {
      event.preventDefault();
      event.stopPropagation();

      const name = nameInput?.value.trim();
      const email = emailInput?.value?.trim() || '';
      const contact = contactInput?.value.trim();
      const education = educationSelect?.value;
      const state = stateSelect?.value;
      const pageurl = document.querySelector('input[name="pageurl"]')?.value || document.getElementById('pageUrl')?.value || '';

      let hasErrors = false;
      let nameFlag = '';
      let emailFlag = '';
      let contactNumberFlag = '';
      let educationFlag = '';
      let stateFlag = '';

      nameFlag = validateName(name);
      if (nameFlag) {
        showFieldError(nameInput, nameFlag, 'text-white');
        hasErrors = true;
      } else {
        clearFieldError(nameInput);
      }

      emailFlag = validateEmailField(email);
      if (emailFlag) {
        showFieldError(emailInput, emailFlag, 'text-white');
        hasErrors = true;
      } else {
        clearFieldError(emailInput);
      }

      contactNumberFlag = validateContact(contact);
      if (contactNumberFlag) {
        showFieldError(contactInput, contactNumberFlag, 'text-white');
        hasErrors = true;
      } else {
        clearFieldError(contactInput);
      }

      educationFlag = validateEducation(education);
      if (educationFlag) {
        showFieldError(educationSelect, educationFlag, 'text-red-500');
        hasErrors = true;
      } else {
        clearFieldError(educationSelect);
      }

      stateFlag = validateState(state);
      if (stateFlag) {
        showFieldError(stateSelect, stateFlag, 'text-red-500');
        hasErrors = true;
      } else {
        clearFieldError(stateSelect);
      }


      if (hasErrors) {
        return;
      }

      const payload = {
        name,
        email,
        mobile: contact,
        course_name: pageurl,
        education,
        state,
      };

      fetch(apiEndpoint, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(payload)
      })
        .then(() => {
          showToast("Thank you! We will contact you soon.");
          form.reset();
          [nameInput, emailInput, contactInput, educationSelect, stateSelect].forEach(clearFieldError);
        })
        .catch(() => {
          showToast("Something went wrong. Please try again later.");
        });
    };

    form.addEventListener("submit", handleSubmit);
    form.dataset.listenerAttached = "true";

    cleanupFn = () => {
      form.removeEventListener("submit", handleSubmit);
      nameInput?.removeEventListener('input', clearNameError);
      nameInput?.removeEventListener('change', clearNameError);
      emailInput?.removeEventListener('input', clearEmailError);
      emailInput?.removeEventListener('change', clearEmailError);
      contactInput?.removeEventListener('input', clearContactError);
      contactInput?.removeEventListener('change', clearContactError);
      educationSelect?.removeEventListener('change', clearEducationError);
      stateSelect?.removeEventListener('change', clearStateError);
      delete form.dataset.listenerAttached;
    };
  };

  const timer = setTimeout(attachHandler, 0);

  return () => {
    clearTimeout(timer);
    cleanupFn?.();
  };
}, [showToast, apiEndpoint]);

  return (
    <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
  );
};

GeneralWebPage.propTypes = {
    htmlContent: PropTypes.any,
}